import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../../service/GetTemplate';
import { endPoints } from '../../../Config';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText, translateUiToken } from '../../../utilities/siteTranslations';
import ReportExportButton from '../ReportExportButton';
import { inReportDateRange, reportDate } from '../reportFilters';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const PartsUsageReport = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const tt = (value) => translateUiToken(value, preferredLanguage);
    const [partsUsage, setPartsUsage] = useState([]);
    const [filteredParts, setFilteredParts] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const loadUsage = async () => {
            // By default, show completed repair usages over all time
            const params = new URLSearchParams({
                usageType: 'repair',
                status: 'completed'
            });
            const response = await handleGetRequest(`sparePart/reports/usage?${params.toString()}`, true, false, dispatch);
            if (response?.data) {
                const list = response.data.map((u, idx) => {
                    // determine unit price from common fields (defensive)
                    const unitPrice = u.unitPrice ?? u.price ?? u.sparePartPrice ?? u.sparePartUnitPrice ?? u.rate ?? 0;
                    const qty = Number(u.quantity || 0);
                    const calculatedTotal = (Number(unitPrice) || 0) * qty;
                    return {
                        id: u._id || idx,
                        partCode: u.sparePartSKU,
                        partName: u.sparePartDescription,
                        category: u.usageType || '-',
                        quantityUsed: qty,
                        // Use backend-provided totalCost if present, otherwise calculate from unit price * qty
                        totalCost: Number(u.totalCost ?? calculatedTotal) || 0,
                        machinesUsed: [u.machineSerialNumber || u.machineDeviceId].filter(Boolean),
                        lastUsed: u.authorizationDate || '',
                        technicianName: `${u.technicianName || ''} ${u.technicianLastName || ''}`.trim(),
                        supervisorName: `${u.supervisorName || ''} ${u.supervisorLastName || ''}`.trim(),
                        status: u.status
                    };
                });
                setPartsUsage(list);
                setFilteredParts(list);
            }
        };
        loadUsage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyDateFilter = () => {
        const filtered = partsUsage.filter((part) => inReportDateRange(part.lastUsed, startDate, endDate));
        setFilteredParts(filtered);
        return filtered;
    };

    const costBodyTemplate = (rowData) => {
        return `$${rowData.totalCost?.toFixed(2) || '0.00'}`;
    };

    const machinesBodyTemplate = (rowData) => {
        return (rowData.machinesUsed || []).join(', ');
    };

    const totalCost = filteredParts.reduce((sum, part) => sum + (part.totalCost || 0), 0);
    const totalQuantity = filteredParts.reduce((sum, part) => sum + (part.quantityUsed || 0), 0);

    return (
        <div>
            <div className="card mb-4">
                <h4 className="mb-3">{t('Date Range Filter')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Start Date')}</label>
                        <Calendar value={startDate} onChange={(e) => setStartDate(e.value)} dateFormat="yy-mm-dd" placeholder={t('Select Start Date')} showIcon className="w-full" />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('End Date')}</label>
                        <Calendar value={endDate} onChange={(e) => setEndDate(e.value)} dateFormat="yy-mm-dd" placeholder={t('Select End Date')} showIcon className="w-full" />
                    </div>
                    <div className="col-12 md:col-3 flex align-items-end">
                        <Button label={t('Apply Filter')} icon="pi pi-filter" onClick={applyDateFilter} className="p-button-primary w-full" />
                    </div>
                    <div className="col-12 md:col-3 flex align-items-end">
                        <Button
                            label={t('Reset')}
                            icon="pi pi-refresh"
                            onClick={() => {
                                setStartDate(null);
                                setEndDate(null);
                                setFilteredParts(partsUsage);
                            }}
                            className="p-button-secondary w-full"
                        />
                    </div>
                </div>
            </div>

            <div className="grid mb-4">
                <div className="col-12 md:col-4">
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary">{filteredParts.length}</div>
                            <div className="text-color-secondary">{t('Parts Used')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-4">
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary">{totalQuantity}</div>
                            <div className="text-color-secondary">{t('Total Quantity Used')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-4">
                    <Card>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary">${totalCost.toFixed(2)}</div>
                            <div className="text-color-secondary">{t('Total Cost')}</div>
                        </div>
                    </Card>
                </div>
            </div>

            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4>{t('Parts Usage Report')}</h4>
                    <ReportExportButton getRows={applyDateFilter} filename="Uso_de_repuestos" columns={[
                        { key: 'partCode', header: t('Part Code') },
                        { key: 'partName', header: t('Part Name'), width: 35 },
                        { key: 'category', header: t('Usage Type'), formatter: t },
                        { key: 'quantityUsed', header: t('Quantity Used') },
                        { key: 'totalCost', header: t('Total Cost') },
                        { key: 'machinesUsed', header: t('Machines Used'), formatter: (value) => (value || []).join(', ') },
                        { key: 'lastUsed', header: t('Last Used'), formatter: (value) => reportDate(value)?.toLocaleDateString(preferredLanguage === 'es' ? 'es-ES' : 'en-US') || '-' },
                        { key: 'technicianName', header: t('Technician') },
                        { key: 'supervisorName', header: t('Supervisor') },
                        { key: 'status', header: t('Status'), formatter: tt }
                    ]} />
                </div>
                <DataTable value={filteredParts} paginator rows={10} rowsPerPageOptions={[5, 10, 20]} emptyMessage={t('No parts usage data found.')} responsiveLayout="scroll">
                    <Column field="partCode" header={t('Part Code')} />
                    <Column field="partName" header={t('Part Name')} />
                    <Column field="category" header={t('Usage Type')} body={(rowData) => t(rowData.category)} />
                    <Column field="quantityUsed" header={t('Quantity Used')} />
                    <Column field="totalCost" header={t('Total Cost')} body={costBodyTemplate} />
                    <Column field="machinesUsed" header={t('Machines Used')} body={machinesBodyTemplate} />
                    <Column field="lastUsed" header={t('Last Used')} body={(row) => reportDate(row.lastUsed)?.toLocaleDateString(preferredLanguage === 'es' ? 'es-ES' : 'en-US') || '-'} />
                    <Column field="technicianName" header={t('Technician')} />
                    <Column field="supervisorName" header={t('Supervisor')} />
                    <Column field="status" header={t('Status')} body={(rowData) => tt(rowData.status)} />
                </DataTable>
            </div>
        </div>
    );
};

export default PartsUsageReport;
